import React from 'react';

/**
 * Compact rollup bar for the top of list pages (Leads, Customers, Approvals,
 * Proforma Invoices, Team). Renders 3–5 metric cards horizontally on wide
 * screens, stacks 2-up on mobile.
 *
 * Design goals:
 *   - Zero layout shift: shows skeleton placeholders while loading.
 *   - Uniform visual language across pages so users learn to scan them
 *     the same way every time.
 *   - Big, bold numbers — the label is small (kind, currency etc). The
 *     value is the star.
 *
 * Every item accepts a rich `sub` node so you can layer secondary context
 * (e.g. "3 pending · oldest 5d") without cluttering the headline number.
 *
 * Usage:
 *   <SummaryCards
 *     testid="leads-summary"
 *     loading={loading}
 *     items={[
 *       { label: 'Pipeline value', value: '₹70,000', kind: 'currency' },
 *       { label: 'Quoted', value: '₹70,800', sub: '3 quotations' },
 *       ...
 *     ]}
 *   />
 */
export default function SummaryCards({ items = [], loading = false, testid = 'summary-cards' }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid={`${testid}-skeleton`}>
        {Array.from({ length: Math.max(items.length || 4, 4) }).map((_, i) => (
          <div key={i} className="medical-card p-4 animate-pulse">
            <div className="h-3 bg-[#D5E8E4] rounded w-20 mb-3" />
            <div className="h-7 bg-[#D5E8E4] rounded w-28" />
          </div>
        ))}
      </div>
    );
  }
  if (!items.length) return null;

  const cols = items.length >= 4 ? 'md:grid-cols-4' :
               items.length === 3 ? 'md:grid-cols-3' :
               items.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1';

  return (
    <div className={`grid grid-cols-2 ${cols} gap-3 mb-6`} data-testid={testid}>
      {items.map((it, i) => <MetricCard key={it.testid || i} {...it} />)}
    </div>
  );
}

function MetricCard({ label, value, sub, icon: Icon, tone = 'default', testid, onClick, active = false, tooltip }) {
  // Tone drives the accent color (left border). Kept subtle — the value itself
  // is the visual focus, not decoration.
  const toneMap = {
    default:  'border-l-[#00a69c]',
    revenue:  'border-l-emerald-500',
    warning:  'border-l-amber-500',
    danger:   'border-l-red-500',
    neutral:  'border-l-slate-400',
  };
  const clickable = typeof onClick === 'function';
  // Interactive cards get a subtle bg fill + strong border + arrow when active,
  // and a hover state so users learn they're clickable. Passive cards stay flat.
  const interactiveClasses = clickable
    ? `cursor-pointer transition transform hover:-translate-y-0.5 hover:shadow-md ${active ? 'bg-[#F4F9F7] ring-2 ring-[#00a69c]' : 'hover:bg-[#F9FCFB]'}`
    : '';
  return (
    <div
      className={`medical-card p-4 border-l-4 ${toneMap[tone] || toneMap.default} ${interactiveClasses}`}
      data-testid={testid || 'summary-card'}
      title={tooltip || undefined}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      aria-pressed={clickable ? active : undefined}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">
        {Icon && <Icon size={12} weight="bold" />}
        {label}
        {active && <span className="ml-auto text-[9px] text-[#00a69c] normal-case tracking-normal font-semibold">● Filtering</span>}
      </div>
      <div className="text-2xl md:text-[1.6rem] font-black text-[#052A23] leading-tight mt-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {value}
      </div>
      {sub && (
        <div className="text-[11px] text-[#6D8E87] mt-1.5 truncate" title={typeof sub === 'string' ? sub : undefined}>
          {sub}
        </div>
      )}
    </div>
  );
}

/**
 * Format INR values compactly.
 *   4_720_000  →  ₹47.2L
 *   150_000_000 → ₹15Cr
 *   47_200     →  ₹47,200
 * Passed a nullish value it renders "—" so the card never shows "NaN" or "0".
 * Set `compact=false` if you always want the full comma-separated form.
 */
export function inr(value, { compact = true } = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (compact) {
    const abs = Math.abs(n);
    if (abs >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(abs >= 10_00_00_000 ? 0 : 1)}Cr`;
    if (abs >= 1_00_000)   return `₹${(n / 1_00_000).toFixed(abs >= 10_00_000 ? 0 : 1)}L`;
  }
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}
